import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import MysticalParticles from './MysticalParticles';
import { Image, Upload, Coins, Users, Tag, Award, Calculator } from 'lucide-react';

const ImageSubmission = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('mystveil_token'));
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    imageUrl: '',
    imageTitle: '',
    imageDescription: '',
    artType: 'digital',
    characterCount: 1,
    backgroundInfo: '',
    bonusChips: 0,
    taggedCharacters: [],
    taggedNPCs: []
  });

  const artTypes = [
    { value: 'digital', label: 'Digital Art', baseChips: 10 },
    { value: 'traditional', label: 'Traditional Art', baseChips: 15 },
    { value: '3d', label: '3D Art', baseChips: 20 },
    { value: 'animation', label: 'Animation', baseChips: 25 }
  ];

  useEffect(() => {
    const loadSubmissions = async () => {
      if (!token) {
        setError('Please log in to view submissions');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await fetch('https://api.4fingerstudios.com/api/mystveil/image-submissions', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 401) {
            setError('Session expired. Please log in again.');
            localStorage.removeItem('mystveil_token');
            setToken(null);
          } else {
            throw new Error(`Failed to load submissions: ${response.status}`);
          }
          return;
        }

        const data = await response.json();
        setSubmissions(data || []);
      } catch (err) {
        setError('Failed to load submissions');
        console.error('Submissions load error:', err);
      } finally {
        setLoading(false);
      }
    };

    loadSubmissions();
  }, [token]);

  const calculateChips = () => {
    const artType = artTypes.find(t => t.value === formData.artType);
    const baseChips = artType ? artType.baseChips : 10;
    const characterBonus = formData.characterCount > 1 ? (formData.characterCount - 1) * 5 : 0;
    const totalChips = baseChips + characterBonus + formData.bonusChips;
    return totalChips;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.imageUrl.trim()) {
      alert('Image URL is required');
      return;
    }

    setSubmitting(true);
    try {
      const totalChips = calculateChips();
      const response = await fetch('https://api.4fingerstudios.com/api/mystveil/submit-image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          totalChips: totalChips
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Failed to submit image: ${response.status}`);
      }

      const newSubmission = await response.json();
      setSubmissions([newSubmission, ...submissions]);
      setShowSubmitForm(false);
      setFormData({
        imageUrl: '',
        imageTitle: '',
        imageDescription: '',
        artType: 'digital',
        characterCount: 1,
        backgroundInfo: '',
        bonusChips: 0,
        taggedCharacters: [],
        taggedNPCs: []
      });
    } catch (err) {
      alert(`Failed to submit image: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex flex-col relative">
        <div className="fixed inset-0 -z-10">
          <img 
            src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
            alt="Mystveil Township Map" 
            className="w-full h-full object-cover opacity-30 blur-sm" 
          />
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
        </div>
        <MysticalParticles />
        <Navbar />
        
        <main className="flex-grow flex items-center justify-center px-4 py-8">
          <div className="mystical-card max-w-md mx-auto text-center">
            <h1 className="mystical-title mb-6">Image Submission</h1>
            <p className="mystical-text mb-6">Please log in to submit artwork</p>
            <Link to="/auth/login" className="mystical-button">
              Login
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col relative">
        <div className="fixed inset-0 -z-10">
          <img 
            src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
            alt="Mystveil Township Map" 
            className="w-full h-full object-cover opacity-30 blur-sm" 
          />
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
        </div>
        <MysticalParticles />
        <Navbar />
        
        <main className="flex-grow flex items-center justify-center px-4 py-8">
          <div className="mystical-card">
            <p className="mystical-text">Loading submissions...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col relative">
        <div className="fixed inset-0 -z-10">
          <img 
            src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
            alt="Mystveil Township Map" 
            className="w-full h-full object-cover opacity-30 blur-sm" 
          />
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
        </div>
        <MysticalParticles />
        <Navbar />
        
        <main className="flex-grow flex items-center justify-center px-4 py-8">
          <div className="mystical-card max-w-md mx-auto text-center">
            <h1 className="mystical-title mb-6">Error</h1>
            <p className="mystical-text mb-6 text-red-600">{error}</p>
            <Link to="/" className="mystical-button">
              Return Home
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Magical Background */}
      <div className="fixed inset-0 -z-10">
        <img 
          src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
          alt="Mystveil Township Map" 
          className="w-full h-full object-cover opacity-30 blur-sm" 
        />
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
      </div>

      {/* Magical Particles */}
      <MysticalParticles />

      {/* Navigation */}
      <Navbar />

      {/* Main Content */}
      <main className="flex-grow px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="mystical-card mb-8">
            <div className="flex items-center justify-between mb-6">
              <h1 className="mystical-title text-black">
                Image Submission
              </h1>
              <button 
                onClick={() => setShowSubmitForm(true)}
                className="mystical-button"
              >
                <Upload className="mr-2" size={20} />
                Submit New Artwork
              </button>
            </div>
            
            <div className="mystical-card bg-white bg-opacity-95 mb-6">
              <h3 className="mystical-subtitle text-black mb-4">Chip Rewards System</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {artTypes.map(artType => (
                  <div key={artType.value} className="text-center">
                    <div className="mystical-card bg-white bg-opacity-95">
                      <h4 className="mystical-subtitle text-black text-sm">{artType.label}</h4>
                      <p className="mystical-text text-purple-800 font-bold">{artType.baseChips} base chips</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mystical-text text-gray-600 text-sm mt-4">
                Additional chips for multiple characters (+5 per extra character) and bonus chips for special content.
              </p>
            </div>
          </div>

          {/* Submit Form */}
          {showSubmitForm && (
            <div className="mystical-card mb-8">
              <h3 className="mystical-subtitle text-black text-xl mb-6">
                Submit New Artwork
              </h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block mystical-text text-black font-bold mb-2">
                      Image URL *
                    </label>
                    <input
                      type="url"
                      value={formData.imageUrl}
                      onChange={(e) => setFormData({...formData, imageUrl: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-700 focus:border-transparent"
                      placeholder="https://example.com/your-artwork.jpg"
                      required
                    />
                  </div>
                  <div>
                    <label className="block mystical-text text-black font-bold mb-2">
                      Art Type *
                    </label>
                    <select
                      value={formData.artType}
                      onChange={(e) => setFormData({...formData, artType: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-700 focus:border-transparent"
                    >
                      {artTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label} ({type.baseChips} chips)
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block mystical-text text-black font-bold mb-2">
                    Image Title
                  </label>
                  <input
                    type="text"
                    value={formData.imageTitle}
                    onChange={(e) => setFormData({...formData, imageTitle: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-700 focus:border-transparent"
                    placeholder="Give your artwork a title"
                  />
                </div>

                <div>
                  <label className="block mystical-text text-black font-bold mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.imageDescription}
                    onChange={(e) => setFormData({...formData, imageDescription: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-700 focus:border-transparent"
                    rows="3"
                    placeholder="Describe your artwork, the scene, or any special details"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block mystical-text text-black font-bold mb-2">
                      Character Count
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={formData.characterCount}
                      onChange={(e) => setFormData({...formData, characterCount: parseInt(e.target.value) || 1})}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-700 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block mystical-text text-black font-bold mb-2">
                      Bonus Chips
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="50"
                      value={formData.bonusChips}
                      onChange={(e) => setFormData({...formData, bonusChips: parseInt(e.target.value) || 0})}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-700 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block mystical-text text-black font-bold mb-2">
                      Background Info
                    </label>
                    <input
                      type="text"
                      value={formData.backgroundInfo}
                      onChange={(e) => setFormData({...formData, backgroundInfo: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-700 focus:border-transparent"
                      placeholder="Background details (optional)"
                    />
                  </div>
                </div>

                {/* Chip Calculation */}
                <div className="mystical-card bg-white bg-opacity-95">
                  <div className="flex items-center space-x-3 mb-4">
                    <Calculator className="text-purple-800" size={24} />
                    <h4 className="mystical-subtitle text-black">Chip Calculation</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="mystical-text text-sm text-gray-600">Base Chips</p>
                      <p className="mystical-text font-bold text-purple-800">
                        {artTypes.find(t => t.value === formData.artType)?.baseChips || 10}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="mystical-text text-sm text-gray-600">Character Bonus</p>
                      <p className="mystical-text font-bold text-purple-800">
                        {formData.characterCount > 1 ? (formData.characterCount - 1) * 5 : 0}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="mystical-text text-sm text-gray-600">Total Chips</p>
                      <p className="mystical-text font-bold text-2xl text-purple-800">
                        {calculateChips()}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-4">
                  <button 
                    type="submit" 
                    disabled={submitting}
                    className="mystical-button"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2" size={20} />
                        Submit for {calculateChips()} Chips
                      </>
                    )}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setShowSubmitForm(false)}
                    className="mystical-button bg-gray-600 hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Submissions History */}
          <div className="mystical-card">
            <h3 className="mystical-subtitle text-black text-xl mb-6">
              Your Submissions
            </h3>
            
            {submissions.length > 0 ? (
              <div className="space-y-6">
                {submissions.map(submission => (
                  <div key={submission.id} className="mystical-card bg-white bg-opacity-95">
                    <div className="flex items-start space-x-4">
                      <img 
                        src={submission.imageUrl} 
                        alt={submission.imageTitle}
                        className="w-24 h-24 rounded-md object-cover flex-shrink-0"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="mystical-subtitle text-black text-lg">
                            {submission.imageTitle || 'Untitled'}
                          </h4>
                          <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                            submission.status === 'approved' ? 'bg-green-100 text-green-800' :
                            submission.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {submission.status}
                          </span>
                        </div>
                        <p className="mystical-text text-gray-600 mb-3">
                          {submission.imageDescription || 'No description provided'}
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="mystical-text text-gray-500">Art Type</p>
                            <p className="mystical-text font-bold">{submission.artType}</p>
                          </div>
                          <div>
                            <p className="mystical-text text-gray-500">Characters</p>
                            <p className="mystical-text font-bold">{submission.characterCount}</p>
                          </div>
                          <div>
                            <p className="mystical-text text-gray-500">Chips Earned</p>
                            <p className="mystical-text font-bold text-purple-800">{submission.totalChips}</p>
                          </div>
                          <div>
                            <p className="mystical-text text-gray-500">Submitted</p>
                            <p className="mystical-text font-bold">
                              {new Date(submission.submittedAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mystical-card bg-white bg-opacity-95 text-center">
                <Image className="mx-auto text-gray-400 mb-4" size={64} />
                <h3 className="mystical-subtitle text-black text-xl mb-4">
                  No Submissions Yet
                </h3>
                <p className="mystical-text text-gray-600 mb-6">
                  Submit your first artwork to start earning chips!
                </p>
                <button 
                  onClick={() => setShowSubmitForm(true)}
                  className="mystical-button"
                >
                  <Upload className="mr-2" size={20} />
                  Submit Your First Artwork
                </button>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <Link to="/profile" className="mystical-button">
              My Profile
            </Link>
            <Link to="/gallery" className="mystical-button">
              View Gallery
            </Link>
            <Link to="/" className="mystical-button">
              Return Home
            </Link>
          </div>
        </div>
      </main>

      {/* Mystical Footer */}
      <footer className="mystical-nav py-6 mt-auto">
        <div className="container mx-auto px-4 text-center">
          <p className="mystical-text font-bold">
            © 2025 4fingerstudios <br /> 
            © 2024 Reckless Dreams. All Rights Reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ImageSubmission;
